import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import { useState, useMemo, useCallback } from "react";
import { getFilteredIdeas, getIdeaColumns } from "../api/ideas";
import { saveIdeaStarred, getViews, addViews } from "../Api";
import { getBagelId } from "../utils";
import { ActiveFilter } from "../components/ideas/filters/FiltersMenu";

const PAGE_SIZE = 50;

interface SortStatus {
  columnAccessor?: string;
  direction?: string;
}

interface UseIdeasParams {
  page?: string | null;
  search?: string;
  activeFilters?: ActiveFilter[];
  sortStatus?: SortStatus;
  isArchived?: boolean;
  selectedView?: any;
  subPageKey?: string;
  pathname?: string;
}

interface UseIdeasReturn {
  ideas: any[];
  loading: boolean;
  error: Error | null;
  totalCount: number;
  refetch: () => void;
  starIdea: (ideaId: string, isStarred: boolean, idea: any) => Promise<void>;
  // Views management
  views: any[];
  viewsLoading: boolean;
  upsertView: (viewData: any) => Promise<any>;
  // Columns management
  columns: any[];
  columnsLoading: boolean;
  // Cache management
  clearCache: () => void;
  invalidateFilters: () => void;
  invalidateSubPageCache: () => void;
  getCacheKey: () => readonly any[];
}

// Query keys factory - more granular for better caching
const ideasKeys = {
  all: ["ideas"] as const,
  lists: () => [...ideasKeys.all, "list"] as const,
  list: (params: {
    search: string;
    activeFilters: ActiveFilter[];
    sort: any;
    page: string;
    isArchived: boolean;
    pathname: string;
    subPageKey?: string;
    viewName?: string;
  }) => [...ideasKeys.lists(), "filtered", params] as const,
  // Helper to invalidate all lists
  invalidateAll: () => [...ideasKeys.lists()] as const,
  views: (subPageKey?: string) => [...ideasKeys.all, "views", subPageKey || "main"] as const,
  columns: () => [...ideasKeys.all, "columns"] as const, // Global - columns come from subpage config, not API per subpage
};

export const useIdeas = (params: UseIdeasParams): UseIdeasReturn => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();
  
  const {
    page = "1",
    search = "",
    activeFilters = [],
    sortStatus = {},
    isArchived = false,
    selectedView,
    subPageKey,
    pathname = ""
  } = params;

  // Create a comprehensive, stable query key based on all parameters for granular caching
  const queryKey = useMemo(() => {
    // Create sorting object for API and cache key
    const sort: any = {};
    if (sortStatus?.columnAccessor) {
      sort[sortStatus.columnAccessor] = sortStatus.direction === "asc" ? -1 : 1;
    }

    // Create a comprehensive, stable representation of filters for caching
    const stableFilters = activeFilters?.map(filter => ({
      parent: filter.parent,
      selected: Array.isArray(filter.selected) 
        ? [...filter.selected].sort() 
        : filter.selected,
      operator: filter.operator,
      type: filter.type,
      name: filter.name,
      query: filter.query
    })).sort((a, b) => {
      // Handle cases where parent might be undefined
      const parentA = a.parent || '';
      const parentB = b.parent || '';
      return parentA.localeCompare(parentB);
    }) || [];

    // Enhanced cache parameters that maintain compatibility but add granularity
    const cacheParams = {
      search: search?.trim() || "",
      activeFilters: stableFilters,
      sort,
      page: page || "1",
      isArchived: !!isArchived,
      pathname: pathname || "",
      subPageKey: subPageKey || "",
      viewName: selectedView?.name || "",
      
      // Additional granular cache properties for better separation
      _meta: {
        // Filter metadata for granular caching
        filterCount: stableFilters.length,
        filterSignature: stableFilters.map(f => 
          `${f.parent}:${f.type}:${f.operator || 'none'}:${Array.isArray(f.selected) ? f.selected.join(',') : f.selected || 'none'}`
        ).join('|'),
        
        // Sorting metadata
        sortColumn: sortStatus?.columnAccessor || 'none',
        sortDirection: sortStatus?.direction || 'none',
        sortActive: !!(sortStatus?.columnAccessor && sortStatus?.direction),
        
        // Pagination metadata
        pageNumber: parseInt(page || "1", 10),
        isFirstPage: (parseInt(page || "1", 10)) === 1,
        
        // View metadata
        viewId: selectedView?._id || selectedView?.id || null,
        viewIsGlobal: selectedView?.isGlobal || false,
        viewIsSubPageView: selectedView?.isSubPageView || false,
        viewFilterCount: selectedView?.filters?.length || 0,
        
        // Context metadata
        isArchived: !!isArchived,
        hasSubPageKey: !!(subPageKey && subPageKey.trim()),
        
        // Cache time slot (rounded to 5 minutes for reasonable cache duration)
        cacheTimeSlot: Math.floor(Date.now() / (5 * 60 * 1000))
      }
    };

    if (process.env.NODE_ENV === 'development') {
      console.log('🔑 Granular cache key generated:', {
        filtersCount: cacheParams._meta.filterCount,
        sortColumn: cacheParams._meta.sortColumn,
        sortDirection: cacheParams._meta.sortDirection,
        page: cacheParams._meta.pageNumber,
        view: cacheParams.viewName || 'none',
        filterSig: cacheParams._meta.filterSignature.substring(0, 50) + (cacheParams._meta.filterSignature.length > 50 ? '...' : ''),
        subPageKey: cacheParams.subPageKey || 'main',
        cacheTimeSlot: cacheParams._meta.cacheTimeSlot
      });
    }

    return ideasKeys.list(cacheParams);
  }, [search, activeFilters, sortStatus, page, isArchived, pathname, subPageKey, selectedView]);

  // Main ideas query
  const {
    data: ideasData,
    isLoading: loading,
    error,
    refetch
  } = useQuery({
    queryKey,
    queryFn: async () => {
      console.log('🔄 Fetching ideas with params:', {
        search,
        activeFilters: activeFilters?.length || 0,
        sortStatus,
        page,
        isArchived,
        viewName: selectedView?.name
      });

      const sort: any = {};
      if (sortStatus?.columnAccessor) {
        sort[sortStatus.columnAccessor] = sortStatus.direction === "asc" ? -1 : 1;
      }

      const sanitizeFilter = (filter: Record<string, any>): Record<string, any> => {
        const { options, operations, childFilters, ...sanitizedFilter } = filter;
        
        if (childFilters && Object.keys(childFilters).length > 0) {
          sanitizedFilter.childFilters = Object.keys(childFilters).reduce(
            (acc: Record<string, any>, key: string) => {
              acc[key] = sanitizeFilter(childFilters[key]);
              return acc;
            },
            {}
          );
        }
        
        return sanitizedFilter;
      };

      // Process active filters (similar to current logic)
      const processedFilters = activeFilters?.map((filter) => sanitizeFilter(filter));

      const response = await getFilteredIdeas(
        {
          search,
          activeFilters: processedFilters,
        },
        sort,
        PAGE_SIZE,
        ((Number(page) || 1) - 1) * PAGE_SIZE,
        selectedView?.name || "",
        auth0,
        isArchived
      );

      console.log('✅ Ideas fetched:', response?.ideas?.length || 0, 'total:', response?.totalCount || 0);
      return response;
    },
    enabled: !!auth0.isAuthenticated,
    staleTime: 30000, // 30 seconds - data is fresh for 30 seconds
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache for 10 minutes
    // Refetch when filters or sorting change
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Don't refetch on component mount if we have fresh data
  });

  // Views query
  const {
    data: viewsData,
    isLoading: viewsLoading,
    refetch: refetchViews
  } = useQuery({
    queryKey: ideasKeys.views(subPageKey),
    queryFn: async () => {
      const response = await getViews(auth0);
      return response.views;
    },
    enabled: !!auth0.isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Columns query - global since columns are derived from subpage config, not fetched per subpage
  const {
    data: columnsData,
    isLoading: columnsLoading,
    error: columnsError,
    refetch: refetchColumns
  } = useQuery({
    queryKey: ideasKeys.columns(),
    queryFn: async () => {
      console.log('🔄 Fetching global idea columns...');
      try {
        const columns = await getIdeaColumns(auth0);
        console.log('✅ Global columns fetched successfully:', columns?.length || 0);
        return columns;
      } catch (error) {
        console.error('❌ Failed to fetch columns:', error);
        throw error;
      }
    },
    enabled: !!auth0.isAuthenticated,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours - columns rarely change
    gcTime: 7 * 24 * 60 * 60 * 1000, // 7 days - keep in cache for a week
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Don't refetch on component mount if we have data
    refetchOnReconnect: false, // Don't refetch when network reconnects
    retry: 1, // Reduce retries for faster debugging
  });

  // Star idea mutation
  const starMutation = useMutation({
    mutationFn: async ({ ideaId, isStarred, idea }: { ideaId: string; isStarred: boolean; idea: any }) => {
      return await saveIdeaStarred(idea, auth0);
    },
    onMutate: async ({ ideaId, isStarred }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(queryKey);

      // Optimistically update the cache
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) return old;
        
        const userId = getBagelId(auth0.user);
        const updatedIdeas = old.ideas.map((item: any) => {
          if (item._id === ideaId) {
            return {
              ...item,
              starred: isStarred ? userId : false,
            };
          }
          return item;
        });

        return {
          ...old,
          ideas: updatedIdeas,
        };
      });

      return { previousData };
    },
    onError: (err, variables, context) => {
      // Revert the optimistic update on error
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Upsert view mutation
  const upsertViewMutation = useMutation({
    mutationFn: async (viewData: any) => {
      return await addViews(viewData, auth0);
    },
    onSuccess: () => {
      // Invalidate and refetch views for the current subpage
      queryClient.invalidateQueries({ queryKey: ideasKeys.views(subPageKey) });
      // Also invalidate views for other subpages to ensure consistency
      queryClient.invalidateQueries({ queryKey: [...ideasKeys.all, "views"] });
    },
  });

  // Memoized return values
  const ideas = useMemo(() => ideasData?.ideas || [], [ideasData?.ideas]);
  const totalCount = useMemo(() => ideasData?.totalCount || 0, [ideasData?.totalCount]);
  const views = useMemo(() => viewsData || [], [viewsData]);
  const columns = useMemo(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🏗️ Columns memo update:', {
        columnsData: columnsData?.length || 0,
        columnsLoading,
        columnsError: columnsError?.message || 'none',
        hasAuth: !!auth0.isAuthenticated
      });
    }
    return columnsData || [];
  }, [columnsData, columnsLoading, columnsError, auth0.isAuthenticated]);

  // Cache management callbacks
  const clearCache = useCallback(() => {
    queryClient.removeQueries({ queryKey: ideasKeys.all });
    console.log('🗑️ Ideas cache cleared');
  }, [queryClient]);

  const invalidateFilters = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ideasKeys.invalidateAll() });
    console.log('🔄 Ideas cache invalidated for filter changes');
  }, [queryClient]);

  const invalidateSubPageCache = useCallback(() => {
    // Invalidate all data for the current subpage
    queryClient.invalidateQueries({ queryKey: ideasKeys.views(subPageKey) });
    queryClient.invalidateQueries({ queryKey: ideasKeys.invalidateAll() });
    console.log('🔄 SubPage cache invalidated for subPageKey:', subPageKey || 'main');
  }, [queryClient, subPageKey]);

  const getCacheKey = useCallback(() => {
    return queryKey;
  }, [queryKey]);

  // Callbacks
  const starIdea = useCallback(
    async (ideaId: string, isStarred: boolean, idea: any) => {
      await starMutation.mutateAsync({ ideaId, isStarred, idea });
    },
    [starMutation]
  );

  const upsertView = useCallback(
    async (viewData: any) => {
      return await upsertViewMutation.mutateAsync(viewData);
    },
    [upsertViewMutation]
  );

  return {
    ideas,
    loading,
    error: error as Error | null,
    totalCount,
    refetch,
    starIdea,
    views,
    viewsLoading,
    upsertView,
    columns,
    columnsLoading,
    // Cache management
    clearCache,
    invalidateFilters,
    invalidateSubPageCache,
    getCacheKey,
  };
}; 